import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, Form, Select, FormInstance, Checkbox } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { ReportCustomersFormValue } from '@/core/domain/reports/people/Report.types';
import { useAddressQuery } from '@/hooks';

import { RangePicker } from '@/components/_common';
import RecommendationSelect from '@/components/_common/Recommendation/RecommendationSelect';
import BrandsInfiniteSelect from '@/components/_common/BrandsInfiniteSelect';
import ModelsInfiniteSelect from '@/components/_common/ModelsInfiniteSelect';
import { GenderTypeReport } from '@/core/domain/people/Customers/Customers.types';

interface Props {
  form?: FormInstance<ReportCustomersFormValue>;
}

function CustomersReportForm({ form }: Props) {
  const intl = useIntl();

  const [type, setType] = useState<string>();

  const { state, city } = useAddressQuery();

  const [selectedBrand, setSelectedBrand] = useState<number>();

  const watchSpecific = Form.useWatch('birthdateSpecific', form);

  const handleBrandChange = (brandId?: number) => {
    setSelectedBrand(!!brandId ? brandId : undefined);

    form?.resetFields(['modelId']);
  };

  const handleFormValuesChange = (
    valuesChanged: ReportCustomersFormValue,
    fieldsValues: ReportCustomersFormValue,
  ) => {
    handleTypeChange(valuesChanged);
    handleStateChange(valuesChanged);

    if (!fieldsValues.uf) {
      form?.resetFields(['city']);
      state.setSelectedStateCode('');
    }
  };

  function handleStateChange(values: ReportCustomersFormValue) {
    if (!values.uf) return;

    form?.resetFields(['city']);

    state.setSelectedStateCode(values.uf);
  }

  const handleTypeChange = (valuesType: ReportCustomersFormValue) => {
    if (!valuesType.personType) setType('');

    setType(valuesType.personType);
  };

  return (
    <Form layout="vertical" form={form} onValuesChange={handleFormValuesChange}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={9}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'customers.modal.report.form.type',
            })}
            name="personType"
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={[
                {
                  value: 'NATURAL',
                  label: intl.formatMessage({
                    id: 'customers.domain.personType.natural',
                  }),
                },
                {
                  value: 'LEGAL',
                  label: intl.formatMessage({
                    id: 'customers.domain.personType.legal',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'customers.modal.report.form.situation',
            })}
            name="active"
          >
            <Select
              style={{ width: '100%' }}
              options={[
                {
                  value: 0,
                  label: intl.formatMessage({
                    id: 'customers.filters.active.options.all',
                  }),
                },
                {
                  value: 1,
                  label: intl.formatMessage({
                    id: 'customers.filters.active.options.active',
                  }),
                },
                {
                  value: 2,
                  label: intl.formatMessage({
                    id: 'customers.filters.active.options.inactive',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={9}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'customers.modal.report.form.sex',
            })}
            name="genderType"
          >
            <Select
              allowClear
              disabled={type === 'LEGAL'}
              style={{ width: '100%' }}
              options={[
                {
                  value: GenderTypeReport.Man,
                  label: intl.formatMessage({
                    id: 'customers.domain.genderType.man',
                  }),
                },
                {
                  value: GenderTypeReport.Woman,
                  label: intl.formatMessage({
                    id: 'customers.domain.genderType.woman',
                  }),
                },
                {
                  value: GenderTypeReport.Genderless,
                  label: intl.formatMessage({
                    id: 'customers.domain.genderType.genderless',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <Form.Item
            label={intl.formatMessage({
              id: 'customers.modal.report.form.birthdayOf',
            })}
            name="birthDates"
          >
            <RangePicker
              format={watchSpecific ? 'DD/MM/YYYY' : 'DD/MM'}
              key={Number(watchSpecific)}
              size="large"
              style={{ width: '100%' }}
              disabled={type === 'LEGAL'}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <Form.Item
            label={intl.formatMessage({
              id: 'customers.modal.report.form.inclusionData',
            })}
            name="creationDates"
          >
            <RangePicker size="large" style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'customers.modal.report.form.state',
            })}
            name="uf"
          >
            <Select
              allowClear
              showSearch
              loading={state.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={state.list.map((s) => ({
                value: s.code,
                label: s.code,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={16}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'customers.modal.report.form.city',
            })}
            name="city"
          >
            <Select
              allowClear
              showSearch
              loading={city.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={city.list.map((c) => ({
                value: c.name,
                label: c.name,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={12}>
          <FloatingFormItem
            name="brandId"
            label={intl.formatMessage({
              id: 'customers.domain.vehicle.brand',
            })}
          >
            <BrandsInfiniteSelect allowClear onChange={handleBrandChange} />
          </FloatingFormItem>
        </Col>

        <Col xs={12}>
          <FloatingFormItem
            name="modelId"
            label={intl.formatMessage({
              id: 'customers.domain.vehicle.model',
            })}
          >
            <ModelsInfiniteSelect
              allowClear
              disabled={!selectedBrand}
              queryFilter={{ brandId: selectedBrand }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem name="recommendationIds" label="Recomendações">
            <RecommendationSelect mode="multiple" />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem name="birthdateSpecific" valuePropName="checked">
            <Checkbox>Data Específica de Aniversário</Checkbox>
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default CustomersReportForm;
